import { useCallback, useEffect, useState } from 'react'
import { Users, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { getUsers, type User } from '@/api/users'
import { getDeviceEnrollments, enrollUsersToDevice, syncAllUsersToDevice, type DeviceEnrollment } from '@/api/devices'
import { ApiError } from '@/lib/api-error'

const STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  pending: 'Sincronizando...',
  blocked: 'Bloqueado',
}

const STATUS_CLASSES: Record<string, string> = {
  active: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400',
  pending: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-400',
  blocked: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400',
}

interface Props {
  deviceId: string
}

export function DeviceUsersTab({ deviceId }: Props) {
  const [users, setUsers] = useState<User[]>([])
  const [enrollments, setEnrollments] = useState<Map<string, DeviceEnrollment>>(new Map())
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [enrolling, setEnrolling] = useState(false)
  const [syncingAll, setSyncingAll] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [usersRes, enrollmentsRes] = await Promise.all([
        getUsers(),
        getDeviceEnrollments(deviceId),
      ])
      setUsers(usersRes.users)
      const map = new Map<string, DeviceEnrollment>()
      for (const e of enrollmentsRes.enrollments) map.set(e.user_id, e)
      setEnrollments(map)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error al cargar datos')
    } finally {
      setLoading(false)
    }
  }, [deviceId])

  useEffect(() => { load() }, [load])

  function toggleSelect(userId: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId); else next.add(userId)
      return next
    })
  }

  function toggleAll() {
    if (selected.size === users.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(users.map(u => u.id)))
    }
  }

  async function handleEnrollSelected() {
    if (selected.size === 0) return
    setEnrolling(true)
    try {
      const res = await enrollUsersToDevice(deviceId, Array.from(selected))
      toast.success(`${res.count} usuario(s) enviados a sincronizar`)
      setSelected(new Set())
      await load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error al enrollar usuarios')
    } finally {
      setEnrolling(false)
    }
  }

  async function handleSyncAll() {
    setSyncingAll(true)
    try {
      const res = await syncAllUsersToDevice(deviceId)
      toast.success(`${res.enrolled} usuario(s) enviados a sincronizar`)
      await load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error al sincronizar todos')
    } finally {
      setSyncingAll(false)
    }
  }

  if (loading) return <p className="text-text-muted text-sm py-4">Cargando usuarios...</p>

  const unenrolledCount = users.filter(u => !enrollments.has(u.id)).length

  return (
    <div className="flex flex-col gap-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-text-muted text-xs">
          {users.length} usuario(s) en BD · {enrollments.size} registrado(s) en este dispositivo
          {unenrolledCount > 0 && ` · ${unenrolledCount} sin enrollar`}
        </p>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button
              onClick={handleEnrollSelected}
              disabled={enrolling}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-background hover:bg-accent-hover disabled:opacity-60 transition-colors"
            >
              <Users className="w-3.5 h-3.5" />
              {enrolling ? 'Enrollando...' : `Enrollar ${selected.size} seleccionado(s)`}
            </button>
          )}
          <button
            onClick={handleSyncAll}
            disabled={syncingAll || users.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-border text-text-muted hover:text-text-primary disabled:opacity-60 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncingAll ? 'animate-spin' : ''}`} />
            {syncingAll ? 'Sincronizando...' : 'Enrollar todos'}
          </button>
        </div>
      </div>

      {users.length === 0 ? (
        <p className="text-text-muted text-sm py-6 text-center">No hay usuarios registrados en la base de datos</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="py-2 pr-3 text-left w-8">
                  <input
                    type="checkbox"
                    checked={selected.size === users.length && users.length > 0}
                    ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < users.length }}
                    onChange={toggleAll}
                    className="rounded"
                    aria-label="Seleccionar todos"
                  />
                </th>
                <th className="py-2 pr-4 text-left text-text-muted text-xs font-medium">Nombre</th>
                <th className="py-2 pr-4 text-left text-text-muted text-xs font-medium">Nº empleado</th>
                <th className="py-2 pr-4 text-left text-text-muted text-xs font-medium">Tipo</th>
                <th className="py-2 text-left text-text-muted text-xs font-medium">Estado en dispositivo</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const enrollment = enrollments.get(user.id)
                const status = enrollment?.status
                return (
                  <tr
                    key={user.id}
                    onClick={() => toggleSelect(user.id)}
                    className="border-b border-border/50 hover:bg-background/50 cursor-pointer transition-colors"
                  >
                    <td className="py-2.5 pr-3">
                      <input
                        type="checkbox"
                        checked={selected.has(user.id)}
                        onChange={() => toggleSelect(user.id)}
                        onClick={e => e.stopPropagation()}
                        className="rounded"
                        aria-label={`Seleccionar ${user.name}`}
                      />
                    </td>
                    <td className="py-2.5 pr-4 text-text-primary font-medium">{user.name}</td>
                    <td className="py-2.5 pr-4 text-text-muted font-mono text-xs">{user.employee_no}</td>
                    <td className="py-2.5 pr-4 text-text-muted text-xs capitalize">{user.user_type ?? 'normal'}</td>
                    <td className="py-2.5">
                      {status ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${STATUS_CLASSES[status] ?? 'text-text-muted'}`}>
                          {STATUS_LABELS[status] ?? status}
                        </span>
                      ) : (
                        <span className="text-text-muted text-xs">Sin enrollar</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
